import { Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";
import { Layout } from "./components/layout/Layout.tsx";
import { I18nProvider } from "./i18n/context.tsx";
import type { Locale } from "./i18n/types.ts";
import { AiPage } from "./pages/AiPage.tsx";
import { AiTeamPage } from "./pages/AiTeamPage.tsx";
import { ClassicPage } from "./pages/ClassicPage.tsx";
import { GolfAiPage } from "./pages/GolfAiPage.tsx";
import { GolfClassicPage } from "./pages/GolfClassicPage.tsx";
import { ScreenshotPage } from "./pages/ScreenshotPage.tsx";
import { SoccerAiPage } from "./pages/SoccerAiPage.tsx";
import { SoccerClassicPage } from "./pages/SoccerClassicPage.tsx";
import { TennisAiPage } from "./pages/TennisAiPage.tsx";
import { TennisClassicPage } from "./pages/TennisClassicPage.tsx";

function LocaleLayout({ locale }: { locale: Locale }) {
  return (
    <I18nProvider locale={locale}>
      <Layout>
        <Outlet />
      </Layout>
    </I18nProvider>
  );
}

/** Redirect index to basketball/classic, preserving locale prefix */
function DefaultRedirect() {
  const { pathname } = useLocation();
  const prefix = pathname.startsWith("/ja") ? "/ja" : "";
  return <Navigate to={`${prefix}/basketball/classic`} replace />;
}

function LocaleRedirect({ to }: { to: string }) {
  const { pathname } = useLocation();
  const prefix = pathname.startsWith("/ja") ? "/ja" : "";
  return <Navigate to={`${prefix}${to}`} replace />;
}

/** Shared route elements rendered under both `/` and `/ja` */
function RouteElements() {
  return (
    <>
      {/* Default → Basketball Classic */}
      <Route index element={<DefaultRedirect />} />

      {/* Basketball */}
      <Route path="basketball" element={<LocaleRedirect to="/basketball/classic" />} />
      <Route path="basketball/classic" element={<ClassicPage />} />
      <Route path="basketball/ai" element={<AiPage />} />
      <Route path="basketball/aiteam" element={<AiTeamPage />} />

      {/* Legacy redirects */}
      <Route path="classic" element={<LocaleRedirect to="/basketball/classic" />} />
      <Route path="ai" element={<LocaleRedirect to="/basketball/ai" />} />
      <Route path="aiteam" element={<LocaleRedirect to="/basketball/aiteam" />} />

      {/* Tennis */}
      <Route path="tennis" element={<LocaleRedirect to="/tennis/classic" />} />
      <Route path="tennis/classic" element={<TennisClassicPage />} />
      <Route path="tennis/ai" element={<TennisAiPage />} />

      {/* Soccer */}
      <Route path="soccer" element={<LocaleRedirect to="/soccer/classic" />} />
      <Route path="soccer/classic" element={<SoccerClassicPage />} />
      <Route path="soccer/ai" element={<SoccerAiPage />} />

      {/* Golf */}
      <Route path="golf" element={<LocaleRedirect to="/golf/classic" />} />
      <Route path="golf/classic" element={<GolfClassicPage />} />
      <Route path="golf/ai" element={<GolfAiPage />} />

      {/* Tools */}
      <Route path="screenshots" element={<ScreenshotPage />} />

      {/* Catch-all */}
      <Route path="*" element={<DefaultRedirect />} />
    </>
  );
}

export function AppRouter() {
  return (
    <Routes>
      {/* English (default) — no prefix */}
      <Route element={<LocaleLayout locale="en" />}>{RouteElements()}</Route>

      {/* Japanese — /ja prefix */}
      <Route path="/ja" element={<LocaleLayout locale="ja" />}>
        {RouteElements()}
      </Route>
    </Routes>
  );
}
